import { pdsLocalStorage } from './pds-local-storage';
import { versionAndPaths } from './version-and-paths';

declare let CKEDITOR;

export interface RichTextEditorConfigurationOptions {
  isReadOnly: boolean;
  showToolbar: boolean;
  collapsibleToolbar: boolean;
  toolbarStartupExpanded: boolean;
  showBottomBar: boolean;
  showCharCount: boolean;
  useAdvancedMode: boolean;
  startupFocus: boolean;
  autoParagraph: boolean;
  showSource: boolean;
}

export interface RichTextEditorToolbarGroupItem {
  name: string;
  items: Array<string>;
}

export class RichTextEditorConfiguration {
  static getConfiguration(options: RichTextEditorConfigurationOptions = {
    // Default values
    isReadOnly: false,
    showToolbar: true,
    collapsibleToolbar: false,
    toolbarStartupExpanded: true,
    showBottomBar: true,
    showCharCount: true,
    useAdvancedMode: false,
    startupFocus: false,
    autoParagraph: false,
    showSource: false
  }): any {

    // Basic Configuration equivalent to no toolbar option
    let configuration: any = {
      toolbar: [{ name: 'emptyToolbar', items: [] }],
      customConfig: '',
      extraPlugins: 'justify,font,colorbutton,autoembed,embedsemantic,image2,wordcount',
      removePlugins: 'image',
      wordcount: {
        showParagraphs: false,
        showWordCount: false,
        showCharCount: false,
        countHTML: false,
        maxCharCount: -1
      },
      bodyClass: 'article-editor',
      format_tags: 'p;h1;h2;h3;pre',
      removeDialogTabs: 'image:advanced;link:advanced',
      readOnly: false,
      allowedContent: false,
      contentsCss: [CKEDITOR.getUrl('contents.css'), (/^http/.test(versionAndPaths.cdnAssetPath) ? '' : window.location.origin + '/') + versionAndPaths.cdnAssetPath + '/css/ckeditor-style-overrides.css'],
      startupFocus: false, // do not change this value to true... instead pass in 'true' to the polymer component because we have that value exposed.
      disableNativeSpellChecker: false,
      linkShowTargetTab: false,
      fontSize_defaultLabel: '16',
      autoParagraph: true,
      language: 'en'
    };

    configuration.autoParagraph = options.autoParagraph;

    // Option to hide the bottom bar, which contains the word counter
    if (!options.showBottomBar) {
      configuration.removePlugins += ',elementspath';
      configuration.resize_enabled = false;
    }

    if (options.startupFocus) {
      configuration.startupFocus = true;
    }

    if (options.isReadOnly) {
      // Read Only Option
      configuration.readOnly = true;
      configuration.allowedContent = true;
    } else {
      // Editable Option

      // Show character count if requested
      if (options.showCharCount) {
        configuration.wordcount.showCharCount = true;
        configuration.wordcount.countHTML = true;
      }

      // Show toolbar if requested
      if (options.showToolbar) {
        // Collapsible Toolbar Option
        configuration.toolbarCanCollapse = options.collapsibleToolbar;
        configuration.toolbarStartupExpanded = options.toolbarStartupExpanded;
        let externalLinkSource: RichTextEditorToolbarGroupItem = {
          name: 'externallinks',
          items: ['Source']
        };

        if (options.useAdvancedMode) {
          // Advanced Toolbar Options
          configuration.toolbar = [
            { name: 'clipboard', items: ['Undo', 'Redo'] },
            { name: 'styles', items: ['Styles', 'Format'] },
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Strike', '-', 'RemoveFormat'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote'] },
            { name: 'links', items: ['Link', 'Unlink'] },
            { name: 'insert', items: ['Image', 'EmbedSemantic', 'Table'] },
            { name: 'tools', items: ['Maximize'] }
          ];

          if (pdsLocalStorage.getPersistedBoolean('debug') || options.showSource) {
            configuration.toolbar.push(externalLinkSource);
          }
          // Styles Editing
          configuration.stylesSet = [
            { name: 'Marker', element: 'span', attributes: { 'class': 'marker' } },
            { name: 'Cited Work', element: 'cite' },
            { name: 'Inline Quotation', element: 'q' },
            { name: 'Special Container', element: 'div', styles: { padding: '5px 10px', background: '#eee', border: '1px solid #ccc' } },
            { name: 'Compact table', element: 'table', attributes: { cellpadding: '5', cellspacing: '0', border: '1', bordercolor: '#ccc' }, styles: { 'border-collapse': 'collapse' } },
            { name: 'Borderless Table', element: 'table', styles: { 'border-style': 'hidden', 'background-color': '#E6E6FA' } },
            { name: 'Square Bulleted List', element: 'ul', styles: { 'list-style-type': 'square' } },
            /* Widget Styles */
            // We use this one to style the brownie picture.
            { name: 'Illustration', type: 'widget', widget: 'image', attributes: { 'class': 'image-illustration' } },
            // Media embed
            { name: '240p', type: 'widget', widget: 'embedSemantic', attributes: { 'class': 'embed-240p' } },
            { name: '360p', type: 'widget', widget: 'embedSemantic', attributes: { 'class': 'embed-360p' } },
            { name: '480p', type: 'widget', widget: 'embedSemantic', attributes: { 'class': 'embed-480p' } },
            { name: '720p', type: 'widget', widget: 'embedSemantic', attributes: { 'class': 'embed-720p' } },
            { name: '1080p', type: 'widget', widget: 'embedSemantic', attributes: { 'class': 'embed-1080p' } }
          ];
        } else {
          // Basic Toolbar Options
          let externalLinks: Array<string> = ['Link', 'Unlink', '-', 'Image', 'PasteFromWord'];
          if (pdsLocalStorage.getPersistedBoolean('debug') || options.showSource) {
            externalLinks.push('Source');
          }
          configuration.toolbar = [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'font', items: ['TextColor', 'BGColor', 'FontSize'] },
            { name: 'formatting', items: ['JustifyLeft', 'JustifyCenter'] },
            { name: 'paragraphstyles', items: ['BulletedList', 'NumberedList', 'Table'] },
            { name: 'externallinks', items: externalLinks }
          ];
        }
      }
    }

    if (options.showSource && typeof CKEDITOR !== 'undefined' && CKEDITOR != null) {
      //REF: https://ckeditor.com/docs/ckeditor4/latest/guide/dev_disallowed_content.html#how-to-allow-everything-except
      //It is not possible to disallow content when the Advanced Content Filter is disabled by setting CKEDITOR.config.allowedContent to true.
      //A similar solution can be achieved by allowing all HTML elements.
      configuration.allowedContent = {
        $1: {
            // Use the ability to specify elements as an object.
            elements: CKEDITOR.dtd,
            attributes: true,
            styles: true,
            classes: true
        }
      };
      //This will allow everything except for the <script> elements and attributes starting from 'on'.
      configuration.disallowedContent = 'script; *[on*]';
    }

    return configuration;
  }
}
